import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Loader2, Trash2, Download, FileSignature, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import Forbidden from "@/pages/Forbidden";

const ALLOWED_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant", "manager", "account_manager", "hr"]);
const STATUS_TR = { draft: "Taslak", active: "Aktif", expired: "Süresi Doldu", terminated: "İptal" };
const STATUS_STYLE = {
    draft: "bg-muted text-muted-foreground",
    active: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    expired: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    terminated: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
};
const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—"; } catch { return iso; } };
const fmtSize = (b) => { if (!b) return "—"; const kb = b / 1024; if (kb < 1024) return `${kb.toFixed(0)} KB`; return `${(kb / 1024).toFixed(2)} MB`; };

function UploadModal({ open, onClose, onSaved, customers }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ title: "", customer_id: "", status: "active", signed_at: "", expires_at: "", notes: "" });
    const [file, setFile] = useState(null);
    const fileRef = useRef(null);
    useEffect(() => { if (open) { setForm({ title: "", customer_id: "", status: "active", signed_at: "", expires_at: "", notes: "" }); setFile(null); } }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Başlık zorunlu.");
        if (!file) return toast.error("Dosya seçin.");
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", form.title);
            if (form.customer_id) fd.append("customer_id", form.customer_id);
            fd.append("status", form.status);
            if (form.signed_at) fd.append("signed_at", form.signed_at);
            if (form.expires_at) fd.append("expires_at", form.expires_at);
            if (form.notes) fd.append("notes", form.notes);
            const { data } = await api.post("/contracts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Sözleşme yüklendi.");
            onSaved && onSaved(data); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl">
                <DialogHeader><DialogTitle>Sözleşme Yükle</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <input required placeholder="Sözleşme başlığı" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contract-title-input" />
                    <select className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                        <option value="">— Müşteri —</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-2">
                        <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <div><label className="block text-[11px] text-muted-foreground mb-1">İmza</label><input type="date" className={inputCls} value={form.signed_at} onChange={(e) => setForm({ ...form, signed_at: e.target.value })} /></div>
                        <div><label className="block text-[11px] text-muted-foreground mb-1">Bitiş</label><input type="date" className={inputCls} value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
                    </div>
                    <textarea rows={2} placeholder="Notlar" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    <div className="rounded-xl border-2 border-dashed border-border p-4 text-center">
                        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="contract-file-input" />
                        <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-[13px] font-semibold hover:bg-muted/70"><Upload className="h-4 w-4" /> {file ? file.name : "Dosya seçin (PDF, JPG, PNG)"}</button>
                        {file && <div className="text-[11px] text-muted-foreground mt-1">{fmtSize(file.size)}</div>}
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="contract-upload-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Yükle</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function ContractsPage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canWrite, setCanWrite] = useState(false);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [customers, setCustomers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (search) params.search = search;
            if (status) params.status = status;
            const { data } = await api.get("/contracts", { params });
            setRows(data.items || []); setCanWrite(!!data.can_write);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [search, status]);
    useEffect(() => { if (user && ALLOWED_ROLES.has(user.role)) load(); }, [load, user]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/customers", { params: { page_size: 200 } });
                setCustomers((data.items || []).map((c) => ({ id: c.id, name: c.name })));
            } catch (err) { console.debug("silent:", err); }
        })();
    }, []);

    const download = async (row) => {
        try {
            const resp = await api.get(`/contracts/${row.id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = row.file_name || `${row.contract_number || "contract"}.bin`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) { toast.error(err?.response?.data?.detail || "İndirilemedi."); }
    };
    const del = async (id) => {
        if (!window.confirm("Bu sözleşme silinsin mi?")) return;
        try { await api.delete(`/contracts/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Silindi."); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };

    if (!user) return null;
    if (!ALLOWED_ROLES.has(user.role)) return <Forbidden />;

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="contracts-title">Sözleşmeler</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Müşteri bazlı imzalanmış sözleşme arşivi. Dosya yükleyip indirebilirsiniz.</p>
                </div>
                {canWrite && <button onClick={() => setModalOpen(true)} className="btn-brand h-11" data-testid="btn-new-contract"><Plus className="h-4 w-4" /> Sözleşme Yükle</button>}
            </div>

            <div className="card-elevated p-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input placeholder="Ara..." value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9`} />
                </div>
                <select className={`${inputCls} max-w-[180px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>
            ) : rows.length === 0 ? (
                <div className="card-elevated p-16 text-center text-muted-foreground">
                    <FileSignature className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    Henüz sözleşme yok. {canWrite && <button className="text-[hsl(var(--brand))] hover:underline font-semibold" onClick={() => setModalOpen(true)}>+ İlk sözleşmeyi yükleyin</button>}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rows.map((c) => (
                        <div key={c.id} className="card-elevated p-4" data-testid={`contract-card-${c.id}`}>
                            <div className="flex items-start gap-2">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] shrink-0"><FileSignature className="h-5 w-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[c.status]}`}>{STATUS_TR[c.status]}</span>
                                        <span className="text-[10px] text-muted-foreground">{c.contract_number}</span>
                                    </div>
                                    <div className="text-[14px] font-bold truncate mt-1">{c.title}</div>
                                    <div className="text-[12px] text-muted-foreground truncate">{c.customer_name || "Müşteri yok"}</div>
                                    <div className="grid grid-cols-2 gap-1 mt-2 text-[11px] text-muted-foreground">
                                        <span>İmza: {fmtDate(c.signed_at)}</span>
                                        <span>Bitiş: {fmtDate(c.expires_at)}</span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1">{c.file_name} • {fmtSize(c.file_size)}</div>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => download(c)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] px-3 py-1.5 text-[12px] font-semibold hover:bg-[hsl(var(--brand))]/20" data-testid={`btn-download-${c.id}`}><Download className="h-3.5 w-3.5" /> İndir</button>
                                {canWrite && <button onClick={() => del(c.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <UploadModal open={modalOpen} onClose={() => setModalOpen(false)} customers={customers}
                onSaved={(x) => setRows((r) => [x, ...r])} />
        </div>
    );
}
